import { DefaultButton, PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';
import { Dialog, DialogFooter } from 'office-ui-fabric-react/lib/Dialog';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { logUsage } from 'owa-analytics';
import hasPendingAttachments from 'owa-attachment-well-data/lib/utils/hasPendingAttachments';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ComposeViewState } from 'owa-mail-compose-store';
import onSmimeOptionsChange from 'owa-mail-compose-store/lib/actions/onSmimeOptionsChange';
import setIsDeliveryReceiptRequested from 'owa-mail-compose-store/lib/actions/setIsDeliveryReceiptRequested';
import setIsReadReceiptRequested from 'owa-mail-compose-store/lib/actions/setIsReadReceiptRequested';
import setMessageSensitivity from 'owa-mail-compose-store/lib/actions/setMessageSensitivity';
import { hasPendingSaveAction } from 'owa-mail-compose-store/lib/actions/trySaveMessage';
import updateSensitivityInfoBar from 'owa-mail-compose-store/lib/actions/updateSensitivityInfoBar';
import { getSensitivityOptions, getSensitivityTypeForKey } from 'owa-mail-sensitivity';
import SensitivityType from 'owa-service/lib/contract/SensitivityType';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { isBrowserSupported } from 'owa-smime-adapter/lib/utils/smimeUtils';
import commonStrings from 'owa-strings/owa-common-strings';
import strings from 'owa-strings/owa-mail-strings';
import * as React from 'react';
const styles = require('./MessageOptionsDialog.scss');

export interface MesageOptionsDialogProps {
    onClose?: () => void;
    composeViewState: ComposeViewState;
}

export interface MesageOptionsDialogState {
    isShown: boolean;
    isReadReceiptRequested: boolean;
    isDeliveryReceiptRequested: boolean;
    sensitivity: SensitivityType;
    shouldEncryptMessageAsSmime: boolean;
    shouldSignMessageAsSmime: boolean;
}

export default class MessageOptionsDialog extends React.Component<
    MesageOptionsDialogProps,
    MesageOptionsDialogState
> {
    constructor(props: MesageOptionsDialogProps) {
        super(props);
        const {
            isReadReceiptRequested,
            isDeliveryReceiptRequested,
            sensitivity,
            smimeViewState,
        } = props.composeViewState;
        this.state = {
            isShown: true,
            isReadReceiptRequested: isReadReceiptRequested,
            isDeliveryReceiptRequested: isDeliveryReceiptRequested,
            sensitivity: sensitivity ? sensitivity : 'Normal',
            shouldEncryptMessageAsSmime: smimeViewState.shouldEncryptMessageAsSmime,
            shouldSignMessageAsSmime: smimeViewState.shouldSignMessageAsSmime,
        };
    }

    private onAcceptChanges = () => {
        const { composeViewState } = this.props;
        const {
            isReadReceiptRequested,
            isDeliveryReceiptRequested,
            shouldEncryptMessageAsSmime,
            shouldSignMessageAsSmime,
            sensitivity,
        } = this.state;

        setIsReadReceiptRequested(composeViewState, isReadReceiptRequested);
        setIsDeliveryReceiptRequested(composeViewState, isDeliveryReceiptRequested);
        onSmimeOptionsChange(
            composeViewState,
            shouldEncryptMessageAsSmime,
            shouldSignMessageAsSmime
        );
        setMessageSensitivity(composeViewState, sensitivity);
        updateSensitivityInfoBar(composeViewState);
        this.onDismiss();
    };

    private onDismiss = () => {
        this.setState({
            isShown: false,
        });
        this.props.onClose();
    };

    private isSmimeOptionDisabled = (): boolean => {
        const { composeViewState } = this.props;
        // Disable S/MIME options toggling if multiple From addresses exist or if there is any pending attachments.
        return (
            composeViewState.fromViewState.isFromShown ||
            hasPendingAttachments(composeViewState.attachmentWell) ||
            hasPendingSaveAction(composeViewState)
        );
    };

    private shouldShowSmimeOptions = (): boolean => {
        return isFeatureEnabled('cmp-smime') && !isConsumer() && isBrowserSupported();
    };

    render() {
        const dialogContent = [];
        const {
            isReadReceiptRequested,
            isDeliveryReceiptRequested,
            smimeViewState,
        } = this.props.composeViewState;
        if (isFeatureEnabled('cmp-setMessageSensitivity')) {
            dialogContent.push(
                <Dropdown
                    className={styles.formElement}
                    defaultSelectedKey={this.state.sensitivity}
                    label={strings.sensitivityLabel}
                    // TODO #31248 move to strings
                    ariaLabel={strings.sensitivityLabel}
                    onChanged={item => {
                        this.setState({
                            sensitivity: getSensitivityTypeForKey(item.key.toString()),
                        });
                    }}
                    options={getSensitivityOptions()}
                />
            );
        }
        if (isFeatureEnabled('cmp-requestReadReceipts')) {
            dialogContent.push(
                <Checkbox
                    defaultChecked={isReadReceiptRequested}
                    label={strings.requestReadReceipts}
                    ariaLabel={strings.requestReadReceipts}
                    onChange={(event: any, checked: boolean) =>
                        this.setState({
                            isReadReceiptRequested: checked,
                        })
                    }
                />
            );
        }
        if (isFeatureEnabled('cmp-requestDeliveryReceipts')) {
            dialogContent.push(
                <Checkbox
                    defaultChecked={isDeliveryReceiptRequested}
                    label={strings.requestDeliveryReceipts}
                    ariaLabel={strings.requestDeliveryReceipts}
                    onChange={(event: any, checked: boolean) =>
                        this.setState({
                            isDeliveryReceiptRequested: checked,
                        })
                    }
                />
            );
        }
        if (this.shouldShowSmimeOptions()) {
            dialogContent.push(
                <Checkbox
                    defaultChecked={smimeViewState.shouldEncryptMessageAsSmime}
                    label={strings.encryptMessageToSmime}
                    ariaLabel={strings.encryptMessageToSmime}
                    disabled={this.isSmimeOptionDisabled()}
                    onChange={(event: any, checked: boolean) =>
                        this.setState({
                            shouldEncryptMessageAsSmime: checked,
                        })
                    }
                />
            );
            dialogContent.push(
                <Checkbox
                    defaultChecked={smimeViewState.shouldSignMessageAsSmime}
                    label={strings.signMessageToSmime}
                    ariaLabel={strings.signMessageToSmime}
                    disabled={this.isSmimeOptionDisabled()}
                    onChange={(event: any, checked: boolean) =>
                        this.setState({
                            shouldSignMessageAsSmime: checked,
                        })
                    }
                />
            );
        }

        if (dialogContent.length === 0) {
            logUsage(
                'MessageOptionsEmptyInvariantViolation',
                null, // customData
                {
                    isQos: true,
                }
            );
        }
        return (
            <Dialog
                hidden={!this.state.isShown}
                dialogContentProps={{
                    title: strings.messageOptionsHeader,
                    className: styles.dialogContent,
                }}
                onDismiss={this.onDismiss}>
                {dialogContent}
                <DialogFooter>
                    <PrimaryButton onClick={this.onAcceptChanges}>
                        {commonStrings.okButton}
                    </PrimaryButton>
                    <DefaultButton onClick={this.onDismiss}>
                        {commonStrings.cancelButton}
                    </DefaultButton>
                    }
                </DialogFooter>
            </Dialog>
        );
    }
}
